import { useState, useCallback, useEffect } from 'react';
import FiltersSection from './components/filters-section';
import ResultsSection from './components/results-section';
import AsyncJobStatusComponent from './components/async-job-status';
import { useLazyExecutarConciliacaoQuery } from '@/services/conciliacao-fiscal';
import { useLazyGetActiveJobQuery, AsyncJobStatus } from '@/services/tax-reconciliation';
import type { ConciliacaoResponseDto, ConciliacaoFilterDto } from '@/services/conciliacao-fiscal';

export default function ConciliacaoFiscalTab() {
  const [results, setResults] = useState<ConciliacaoResponseDto | undefined>();
  const [loading, setLoading] = useState(false);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [currentFilters, setCurrentFilters] = useState<ConciliacaoFilterDto | undefined>(undefined);

  const [executarConciliacao] = useLazyExecutarConciliacaoQuery();
  const [getActiveJob] = useLazyGetActiveJobQuery();

  const isJobActive = (status?: string) =>
    status === AsyncJobStatus.PENDING || status === AsyncJobStatus.PROCESSING;

  // On mount: check if there's an active job to recover (F5-safe)
  useEffect(() => {
    getActiveJob()
      .unwrap()
      .then((activeJob: any) => {
        if (activeJob?.jobId && isJobActive(activeJob?.status)) {
          setActiveJobId(activeJob.jobId);
        }
      })
      .catch(() => {
        // No active job — normal state
      });
  }, []);

  const checkActiveJob = async (): Promise<boolean> => {
    try {
      const activeJob = await getActiveJob().unwrap() as any;
      if (activeJob?.jobId && isJobActive(activeJob?.status)) {
        setActiveJobId(activeJob.jobId);
        setResults(undefined);
        setLoading(false);
        return true;
      }
    } catch {
      // No active job
    }
    return false;
  };

  const handleExecuteStart = async () => {
    // Check if there's already an active job running
    const hasActive = await checkActiveJob();
    if (hasActive) return;

    // No active job — proceed with new execution
    setResults(undefined);
    setActiveJobId(null);
    setLoading(true);
  };

  const handleExecute = (data: any, filters?: ConciliacaoFilterDto) => {
    if (filters) {
      setCurrentFilters(filters);
    }

    // Detect async response: PT module returns {async:true, jobId}, EN module returns {jobId, statusUrl}
    const jobId = data?.jobId;
    if ((data?.async && jobId) || (jobId && data?.statusUrl)) {
      setActiveJobId(jobId);
      setResults(undefined);
    } else {
      // Sync processing - show results immediately
      setResults(data);
      setActiveJobId(null);
    }
    setLoading(false);
  };

  const handleExecuteError = () => {
    setLoading(false);
  };

  const handlePageChange = useCallback(async (page: number) => {
    if (!currentFilters) return;

    try {
      setLoading(true);
      const params = {
        ...currentFilters,
        page,
      };

      const result = await executarConciliacao(params).unwrap();
      setResults(result);
    } catch (error) {
      console.error('Erro ao mudar página:', error);
    } finally {
      setLoading(false);
    }
  }, [currentFilters, executarConciliacao]);

  const handleJobComplete = (result: ConciliacaoResponseDto) => {
    setResults(result);
    setActiveJobId(null);
  };

  const handleJobCancel = () => {
    setActiveJobId(null);
    setLoading(false);
  };

  const handleClearResults = () => {
    setResults(undefined);
    setCurrentFilters(undefined);
    setActiveJobId(null);
  };

  return (
    <div className="space-y-6">
      <FiltersSection
        onExecuteStart={handleExecuteStart}
        onExecute={handleExecute}
        onExecuteError={handleExecuteError}
        loading={loading}
        onClearResults={handleClearResults}
      />

      {activeJobId ? (
        <AsyncJobStatusComponent
          jobId={activeJobId}
          onComplete={handleJobComplete}
          onCancel={handleJobCancel}
        />
      ) : (
        <ResultsSection
          data={results}
          onPageChange={handlePageChange}
          loading={loading}
          currentFilters={currentFilters}
        />
      )}
    </div>
  );
}